import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkIcon from '@mui/icons-material/Link';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { COVER_MAX_BYTES, IMAGE_TYPES, refuseFile } from '@/lib/images';
import { testIdAttr } from '@/lib/testIds';

import {
  ADDRESS_RULE,
  allowedAddress,
  isEmptyDocument,
  richTextExtensions,
  textLength,
  type ImageAddress,
} from './richText';

/** How the editor shows and stores images; without it, it offers none. */
export interface RichTextImages {
  /** The address to show a stored key by, or null when none is known. */
  address: ImageAddress;
  /** Stores a file and gives its key; a refusal is thrown with its sentence. */
  upload: (file: File) => Promise<{ key: string }>;
}

interface RichTextFieldProps {
  label: string;
  hint?: string;
  /** The stored document, or null for none. */
  value: JSONContent | null;
  /** Called with the new document, or null when it holds nothing. */
  onChange: (value: JSONContent | null) => void;
  /** The server's sentence for a refused document. */
  error?: string;
  disabled?: boolean;
  /** Read once, when the editor is made: pass the same object every time. */
  images?: RichTextImages;
  /** The most characters of text the server takes: its setting, from the reply's limits. */
  maxLength: number;
  /** `<testId>-input` is the editable area; the toolbar's controls are named from it. */
  testId: string;
}

type Panel = 'link' | 'cta' | 'image' | null;

const PANEL_NAMES: Record<Exclude<Panel, null>, string> = {
  link: 'Link',
  cta: 'Call-to-action button',
  image: 'Image',
};

interface FormatButtonProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  disabled: boolean;
  /** Only one toolbar control is in the Tab order at a time. */
  tabbable: boolean;
  onClick: () => void;
  onFocus: () => void;
  testId: string;
}

/** One toolbar control: named in words, pressed while its format applies. */
function FormatButton({
  label,
  icon,
  active,
  disabled,
  tabbable,
  onClick,
  onFocus,
  testId,
}: FormatButtonProps) {
  return (
    <Tooltip title={label}>
      <span>
        <ToggleButton
          value={label}
          size="small"
          selected={active}
          disabled={disabled}
          onChange={onClick}
          onFocus={onFocus}
          tabIndex={tabbable ? 0 : -1}
          aria-label={label}
          data-toolbar-button=""
          data-testid={testId}
          sx={{ px: 1, py: 0.5, border: 0, minWidth: 36 }}
        >
          {icon}
        </ToggleButton>
      </span>
    </Tooltip>
  );
}

/**
 * The rich text editor: TipTap, offering exactly the backend's allowed list
 * (`richText.ts`), so it cannot produce a document the server refuses.
 *
 * Each toolbar control is named in words and pressed while its format
 * applies. The toolbar is one stop in the Tab order: the arrow keys, Home
 * and End move between its controls, as the toolbar pattern asks. A link, a
 * call-to-action button and an image are added in a small panel under it
 * rather than a dialog, because the editor itself sits in one. An image is
 * stored the moment it is inserted, and needs a description. The count under
 * the box says how much of `maxLength` is used.
 */
export function RichTextField({
  label,
  hint,
  value,
  onChange,
  error,
  disabled = false,
  images,
  maxLength,
  testId,
}: RichTextFieldProps) {
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [address, setAddress] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [sending, setSending] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  // The image panel's file problems, apart from its description problems:
  // each is shown by the control it is about, so a screen reader is not told
  // the description is wrong when it is the file that is missing.
  const [fileError, setFileError] = useState<string | null>(null);
  const [focused, setFocused] = useState(0);

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const editor = useEditor({
    extensions: richTextExtensions(images?.address),
    content: value ?? '',
    editable: !disabled,
    onUpdate: ({ editor: current }) => {
      const doc = current.getJSON();
      onChange(isEmptyDocument(doc) ? null : doc);
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': 'true' } : {}),
        'data-testid': `${testId}-input`,
      },
    },
  });

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      h2: current?.isActive('heading', { level: 2 }) ?? false,
      h3: current?.isActive('heading', { level: 3 }) ?? false,
      bold: current?.isActive('bold') ?? false,
      italic: current?.isActive('italic') ?? false,
      bullets: current?.isActive('bulletList') ?? false,
      numbers: current?.isActive('orderedList') ?? false,
      link: current?.isActive('link') ?? false,
      length: textLength(current?.getJSON()),
    }),
  });

  const chain = () => editor?.chain().focus();

  const openPanel = (next: Panel) => {
    setPanelError(null);
    setButtonLabel('');
    setFile(null);
    setAlt('');
    setAddress(
      next === 'link' ? ((editor?.getAttributes('link').href as string) ?? '') : '',
    );
    setPanel(panel === next ? null : next);
  };

  const applyLink = () => {
    if (!allowedAddress(address)) {
      setPanelError(ADDRESS_RULE);
      return;
    }
    const href = address.trim();
    if (editor?.state.selection.empty && !editor.isActive('link')) {
      chain()
        ?.insertContent({
          type: 'text',
          text: href,
          marks: [{ type: 'link', attrs: { href } }],
        })
        .run();
    } else {
      chain()?.extendMarkRange('link').setLink({ href }).run();
    }
    setPanel(null);
  };

  const removeLink = () => {
    chain()?.extendMarkRange('link').unsetLink().run();
    setPanel(null);
  };

  const insertButton = () => {
    if (!buttonLabel.trim()) {
      setPanelError('Give the button a label');
      return;
    }
    if (!allowedAddress(address)) {
      setPanelError(ADDRESS_RULE);
      return;
    }
    chain()
      ?.insertContent({
        type: 'ctaButton',
        attrs: { label: buttonLabel.trim(), href: address.trim() },
      })
      .run();
    setPanel(null);
  };

  const insertImage = async () => {
    if (!file) {
      setFileError('Choose an image');
      return;
    }
    const refusal = refuseFile(file, COVER_MAX_BYTES);
    if (refusal) {
      setFileError(refusal);
      return;
    }
    setFileError(null);
    if (!alt.trim()) {
      setPanelError('Describe the image for people who cannot see it');
      return;
    }
    if (!images) return;
    setPanelError(null);
    setSending(true);
    try {
      const { key } = await images.upload(file);
      chain()
        ?.insertContent({ type: 'image', attrs: { key, alt: alt.trim() } })
        .run();
      setPanel(null);
    } catch (cause) {
      setPanelError(
        cause instanceof Error ? cause.message : 'The image could not be stored.',
      );
    } finally {
      setSending(false);
    }
  };

  const off = disabled || !editor;
  const length = state?.length ?? 0;
  const over = length > maxLength;

  const controls: Omit<FormatButtonProps, 'tabbable' | 'onFocus'>[] = [
    {
      label: 'Heading',
      icon: <Typography sx={{ fontWeight: 700, fontSize: 14 }}>H2</Typography>,
      active: state?.h2 ?? false,
      disabled: off,
      onClick: () => chain()?.toggleHeading({ level: 2 }).run(),
      testId: `${testId}-heading-2-button`,
    },
    {
      label: 'Subheading',
      icon: <Typography sx={{ fontWeight: 700, fontSize: 14 }}>H3</Typography>,
      active: state?.h3 ?? false,
      disabled: off,
      onClick: () => chain()?.toggleHeading({ level: 3 }).run(),
      testId: `${testId}-heading-3-button`,
    },
    {
      label: 'Bold',
      icon: <FormatBoldIcon fontSize="small" />,
      active: state?.bold ?? false,
      disabled: off,
      onClick: () => chain()?.toggleBold().run(),
      testId: `${testId}-bold-button`,
    },
    {
      label: 'Italic',
      icon: <FormatItalicIcon fontSize="small" />,
      active: state?.italic ?? false,
      disabled: off,
      onClick: () => chain()?.toggleItalic().run(),
      testId: `${testId}-italic-button`,
    },
    {
      label: 'Bulleted list',
      icon: <FormatListBulletedIcon fontSize="small" />,
      active: state?.bullets ?? false,
      disabled: off,
      onClick: () => chain()?.toggleBulletList().run(),
      testId: `${testId}-bullet-list-button`,
    },
    {
      label: 'Numbered list',
      icon: <FormatListNumberedIcon fontSize="small" />,
      active: state?.numbers ?? false,
      disabled: off,
      onClick: () => chain()?.toggleOrderedList().run(),
      testId: `${testId}-numbered-list-button`,
    },
    {
      label: 'Link',
      icon: <LinkIcon fontSize="small" />,
      active: (state?.link ?? false) || panel === 'link',
      disabled: off,
      onClick: () => openPanel('link'),
      testId: `${testId}-link-button`,
    },
    {
      label: 'Call-to-action button',
      icon: <SmartButtonIcon fontSize="small" />,
      active: panel === 'cta',
      disabled: off,
      onClick: () => openPanel('cta'),
      testId: `${testId}-cta-button`,
    },
    ...(images
      ? [
          {
            label: 'Image',
            icon: <ImageOutlinedIcon fontSize="small" />,
            active: panel === 'image',
            disabled: off,
            onClick: () => openPanel('image'),
            testId: `${testId}-image-button`,
          },
        ]
      : []),
  ];

  /** Arrow keys, Home and End move between the toolbar's controls. */
  const moveFocus = (event: KeyboardEvent<HTMLElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-toolbar-button]'),
    );
    const at = buttons.findIndex((button) => button === document.activeElement);
    if (at < 0) return;
    event.preventDefault();
    const last = buttons.length - 1;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowRight'
            ? (at + 1) % buttons.length
            : (at - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  const apply =
    panel === 'link'
      ? applyLink
      : panel === 'cta'
        ? insertButton
        : () => void insertImage();

  return (
    <Box data-testid={testId}>
      <Typography id={labelId} component="span" variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {hint ? (
        <Typography
          id={hintId}
          variant="body2"
          color="text.secondary"
          data-testid={`${testId}-hint`}
        >
          {hint}
        </Typography>
      ) : null}

      <Paper variant="outlined" sx={{ mt: 1, overflow: 'hidden' }}>
        <Box
          role="toolbar"
          aria-label={`${label} formatting`}
          data-testid={`${testId}-toolbar`}
          onKeyDown={moveFocus}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {controls.map((control, index) => (
            <FormatButton
              key={control.testId}
              {...control}
              tabbable={index === Math.min(focused, controls.length - 1)}
              onFocus={() => setFocused(index)}
            />
          ))}
        </Box>

        {panel ? (
          <Box
            role="group"
            aria-label={PANEL_NAMES[panel]}
            data-testid={`${testId}-${panel}-panel`}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-start',
              p: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {panel === 'cta' ? (
              <TextField
                label="Button label"
                size="small"
                value={buttonLabel}
                onChange={(event) => setButtonLabel(event.target.value)}
                slotProps={{ htmlInput: testIdAttr(`${testId}-cta-label-input`) }}
                sx={{ flex: '1 1 10rem' }}
              />
            ) : null}
            {panel === 'image' ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: '1 1 10rem',
                    minWidth: 0,
                    pt: 0.25,
                  }}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={sending}
                    onClick={() => fileInput.current?.click()}
                    data-testid={`${testId}-image-choose-button`}
                  >
                    {file ? 'Choose another' : 'Choose image'}
                  </Button>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    data-testid={`${testId}-image-file-name`}
                  >
                    {file ? file.name : 'No image chosen'}
                  </Typography>
                  <input
                    ref={fileInput}
                    type="file"
                    accept={IMAGE_TYPES.join(',')}
                    hidden
                    aria-hidden
                    tabIndex={-1}
                    data-testid={`${testId}-image-file-input`}
                    onChange={(event) => {
                      setFile(event.target.files?.[0] ?? null);
                      setPanelError(null);
                      setFileError(null);
                      event.target.value = '';
                    }}
                  />
                  {fileError ? (
                    <Typography
                      variant="caption"
                      color="error"
                      role="alert"
                      data-testid={`${testId}-image-file-error`}
                    >
                      {fileError}
                    </Typography>
                  ) : null}
                </Box>
                <TextField
                  label="Description (alternative text)"
                  size="small"
                  required
                  value={alt}
                  onChange={(event) => setAlt(event.target.value)}
                  error={Boolean(panelError)}
                  helperText={panelError ?? 'Read out to people who cannot see the image'}
                  slotProps={{
                    htmlInput: testIdAttr(`${testId}-image-alt-input`),
                    formHelperText: testIdAttr(
                      panelError ? `${testId}-image-error` : `${testId}-image-alt-hint`,
                    ),
                  }}
                  sx={{ flex: '2 1 14rem' }}
                />
              </>
            ) : (
              <TextField
                label={panel === 'link' ? 'Link address' : 'Button address'}
                placeholder="https://"
                size="small"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                error={Boolean(panelError)}
                helperText={panelError ?? undefined}
                slotProps={{
                  htmlInput: testIdAttr(`${testId}-${panel}-address-input`),
                  formHelperText: testIdAttr(`${testId}-${panel}-error`),
                }}
                sx={{ flex: '2 1 14rem' }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1, pt: 0.25 }}>
              <Button
                size="sm"
                onClick={apply}
                loading={sending}
                data-testid={`${testId}-${panel}-apply-button`}
              >
                {panel === 'link' ? 'Apply' : 'Insert'}
              </Button>
              {panel === 'link' && state?.link ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={removeLink}
                  data-testid={`${testId}-link-remove-button`}
                >
                  Remove link
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                disabled={sending}
                onClick={() => setPanel(null)}
                data-testid={`${testId}-${panel}-cancel-button`}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            '& .ProseMirror': {
              minHeight: 160,
              maxHeight: 360,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              outline: 'none',
              '& > *:first-of-type': { mt: 0 },
              '& h2': { fontSize: '1.25rem', fontWeight: 600, my: 1 },
              '& h3': { fontSize: '1.05rem', fontWeight: 600, my: 1 },
              '& p': { my: 0.75 },
              '& a': { color: 'primary.main' },
            },
            '& .ProseMirror:focus-visible': {
              boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
            },
            '& .cta-button': {
              display: 'inline-block',
              my: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600,
            },
            '& .arc-image': {
              display: 'block',
              maxWidth: '100%',
              maxHeight: 240,
              my: 1,
              borderRadius: 1,
            },
            '& .ProseMirror-selectednode:is(.cta-button, .arc-image)': {
              outline: '2px solid',
              outlineColor: 'primary.dark',
              outlineOffset: 2,
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
        <Typography
          id={errorId}
          variant="body2"
          color="error"
          role={error ? 'alert' : undefined}
          data-testid={error ? `${testId}-error` : undefined}
        >
          {error ?? ''}
        </Typography>
        <Typography
          variant="caption"
          color={over ? 'error' : 'text.secondary'}
          data-testid={`${testId}-count`}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {/* As the server writes numbers, whatever the browser's language. */}
          {length.toLocaleString('en-US')} of {maxLength.toLocaleString('en-US')}{' '}
          characters
        </Typography>
      </Box>
    </Box>
  );
}
